import { Box, CircularProgress, useTheme } from "@mui/material";
import FrostedCard from "./FrostedCard";
import StatusIcon from "./StatusIcon";
import { useT } from "src/i18n";

export function ResultRow({ tone, title, body, meta, action }) {
    const theme = useTheme();
    const tints = theme.custom?.wizard?.stateTints ?? {
        success: { title: "#7BC598", discFill: "rgba(123,197,152,0.15)", discBorder: "rgba(123,197,152,0.40)" },
        warn:    { title: "#E8C257", discFill: "rgba(232,194,87,0.14)",  discBorder: "rgba(232,194,87,0.40)"  },
        error:   { title: "#E07A6B", discFill: "rgba(224,122,107,0.14)", discBorder: "rgba(224,122,107,0.40)" },
    };
    const tint = tints[tone];
    const motion = theme.custom?.wizard?.motion ?? { entrance: "360ms", easing: "cubic-bezier(0.22,1,0.36,1)" };

    return (
        <FrostedCard
            tone={tone}
            sx={{
                mt: 1,
                p: "18px 20px",
                display: "flex",
                alignItems: "flex-start",
                gap: 1.75,
                opacity: 0,
                transform: "translateY(8px)",
                animation: `feasibilityIn ${motion.entrance} ${motion.easing} forwards`,
                "@keyframes feasibilityIn": {
                    to: { opacity: 1, transform: "translateY(0)" },
                },
                "@media (prefers-reduced-motion: reduce)": {
                    animation: "none",
                    opacity: 1,
                    transform: "none",
                },
            }}
        >
            <Box sx={{
                width: 36, height: 36, flexShrink: 0,
                borderRadius: "50%",
                backgroundColor: tint.discFill,
                border: `1px solid ${tint.discBorder}`,
                display: "flex", alignItems: "center", justifyContent: "center",
            }}>
                <StatusIcon tone={tone} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{
                    fontFamily: "'Black Ops One', serif",
                    fontSize: 14,
                    letterSpacing: "0.06em",
                    color: tint.title,
                    mb: 0.5,
                    textTransform: "uppercase",
                }}>{title}</Box>
                <Box sx={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 14,
                    color: "text.secondary",
                    lineHeight: 1.5,
                }}>{body}</Box>
                {meta && (
                    <Box sx={{
                        mt: 0.5,
                        fontFamily: "'Inter', sans-serif",
                        fontSize: 12,
                        color: "text.secondary",
                        opacity: 0.85,
                    }}>{meta}</Box>
                )}
                {action && <Box sx={{ mt: 1 }}>{action}</Box>}
            </Box>
        </FrostedCard>
    );
}

export default function AddressFeasibilityPanel({ status, feasibility, error, onRetry }) {
    const theme = useTheme();
    const amber = theme.custom?.wizard?.amber ?? "#E8893B";
    const t = useT();
    if (status === "checking") {
        return (
            <FrostedCard sx={{ mt: 1, p: "14px 18px", display: "flex", alignItems: "center", gap: 1.5 }}>
                <CircularProgress size={18} />
                <Box sx={{ fontFamily: "'Inter', sans-serif", fontSize: 14, color: "text.secondary" }}>
                    {t("wizard.addressFeasibility.checking")}
                </Box>
            </FrostedCard>
        );
    }
    if (status === "error") {
        return (
            <ResultRow
                tone="error"
                title={t("wizard.addressFeasibility.errorTitle")}
                body={error?.message || t("wizard.addressFeasibility.errorFallback")}
                action={
                    <Box
                        component="button"
                        type="button"
                        onClick={onRetry}
                        sx={{
                            background: "none",
                            border: "none",
                            color: "text.primary",
                            font: "inherit",
                            textDecoration: "underline",
                            cursor: "pointer",
                            padding: 0,
                            borderRadius: "6px",
                            "&:focus-visible": {
                                outline: `2px solid ${amber}`,
                                outlineOffset: 2,
                            },
                        }}
                    >
                        {t("wizard.addressFeasibility.retry")}
                    </Box>
                }
            />
        );
    }
    if (!feasibility) return null;
    if (feasibility.geocoded === false) {
        return (
            <ResultRow
                tone="error"
                title={t("wizard.addressFeasibility.notFoundTitle")}
                body={t("wizard.addressFeasibility.notFoundBody")}
            />
        );
    }
    if (feasibility.deliverable === false) {
        return (
            <ResultRow
                tone="warn"
                title={t("wizard.addressFeasibility.noSupplyTitle")}
                body={t("wizard.addressFeasibility.noSupplyBody")}
            />
        );
    }
    const count = feasibility.providerCount;
    const km = feasibility.nearestVarikkoKm;
    const meta = km != null ? t("wizard.addressFeasibility.nearestVarikko", { km: km.toFixed ? km.toFixed(0) : km }) : null;
    const unit = t(count === 1 ? "wizard.units.pumppufirmaOne" : "wizard.units.pumppufirmaOther");
    return (
        <ResultRow
            tone="success"
            title={t("wizard.addressFeasibility.successTitle")}
            body={t("wizard.addressFeasibility.successBody", { count, unit })}
            meta={meta}
        />
    );
}
